// src/components/.../TopBar.tsx
import React, { useRef, useEffect, useState } from 'react';
import { Toast } from 'primereact/toast';
import { useOptionFiles } from '../../stateManagers/hooks/useOptionFiles';
import { AuthUser } from '../../stateManagers/store/authStore';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import SetupStepperModal from '../Setup/SetupStepperModal';
import { showDevTools } from '../../config/env';

interface TopBarProps {
  user: AuthUser | null;
}

const getInitials = (role: string) => {
  if (!role) return 'U';
  return role.substring(0, 2).toUpperCase();
};

const TopBar: React.FC<TopBarProps> = ({ user }) => {
  const toast = useRef<Toast>(null);
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);

  // ── Use the hook to get school logo ──
  const { optionFiles, loading: filesLoading } = useOptionFiles([
    "school_logo",
  ]);

  const { schoolDetails, fetchSchoolDetails } = useSchoolDetailsStore();

  useEffect(() => {
    if (user?.schoolId) {
      fetchSchoolDetails(user.schoolId);
    }
  }, [user?.schoolId, fetchSchoolDetails]);

  // Get logo URL from the hook response
  const logoUrl = optionFiles.school_logo?.files?.[0]?.url ?? null;
  const schoolName = schoolDetails?.name ?? null;

  return (
    <>
      <Toast ref={toast} />

      <div
        style={{
          height: '70px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderBottom: '1px solid rgba(0,0,0,0.07)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 28px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}
      >

        {/* ── Left: School name ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            fontSize: '1.05rem',
            fontWeight: 700,
            color: '#1e3a8a',
            letterSpacing: '0.01em',
            whiteSpace: 'nowrap',
          }}>
            {schoolName || 'SkoolPillar'}
          </span>
        </div>

        {/* ── Right: Setup, Settings, Notifications, User, School Logo ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>

          {/* Setup status – only shown when showDevTools is true */}
          {showDevTools && (
            <button
              onClick={() => setIsSetupModalOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                background: '#eff6ff',
                border: '1px solid rgba(59,130,246,0.25)',
                borderRadius: 10,
                padding: '7px 13px',
                cursor: 'pointer',
                color: '#1d4ed8',
                fontSize: 12.5,
                fontWeight: 700,
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#dbeafe'; }}
              onMouseOut={e => { e.currentTarget.style.background = '#eff6ff'; }}
            >
              <i className="pi pi-verified" style={{ fontSize: 15 }} />
              Setup
            </button>
          )}

          {/* Settings */}
          <button
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 8 }}
            onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <i className="pi pi-cog" style={{ fontSize: 20, color: '#6b7280' }} />
          </button>

          {/* Notifications */}
          <button
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 8, position: 'relative' }}
            onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <i className="pi pi-bell" style={{ fontSize: 20, color: '#6b7280' }} />
            <span style={{
              position: 'absolute', top: 6, right: 6,
              width: 8, height: 8,
              background: '#ef4444', borderRadius: '50%',
              border: '2px solid white',
            }} />
          </button>

          {/* Divider */}
          <div style={{ width: 1, height: 28, background: 'rgba(0,0,0,0.1)' }} />

          {/* User info + avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#1f2937', fontSize: 14, fontWeight: 600 }}>
                {user?.role || 'User'}
              </div>
            </div>
            <div style={{
              width: 38, height: 38, borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 15, fontWeight: 600, flexShrink: 0,
            }}>
              {user?.role ? getInitials(user.role) : 'U'}
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: 1, height: 28, background: 'rgba(0,0,0,0.1)' }} />

          {/* School logo */}
          {!filesLoading && logoUrl ? (
            <img
              src={logoUrl}
              alt="School logo"
              style={{
                width: 42, height: 42,
                borderRadius: 10,
                objectFit: 'contain',
                background: 'white',
                border: '1px solid rgba(0,0,0,0.08)',
                padding: 2,
                flexShrink: 0,
              }}
            />
          ) : (
            <div style={{
              width: 42, height: 42, borderRadius: 10, flexShrink: 0,
              background: '#1e3a8a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
            }}>
              SP
            </div>
          )}

        </div>
      </div>

      <SetupStepperModal open={isSetupModalOpen} onClose={() => setIsSetupModalOpen(false)} />
    </>
  );
};

export default TopBar;